import { Box, Typography, Paper, IconButton } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, KeyboardArrowLeft, KeyboardArrowRight, KeyboardReturn, Search, Close, ExpandLess } from '@mui/icons-material';
import { useState, useEffect } from 'react';

interface AtalhosTecladoProps {
  modoTeclado: boolean;
  filtroFocado: boolean;
}

export const AtalhosTeclado = ({ modoTeclado, filtroFocado }: AtalhosTecladoProps) => {
  const [minimizado, setMinimizado] = useState(false);
  
  // Carregar estado do localStorage ao inicializar
  useEffect(() => {
    const estadoSalvo = localStorage.getItem('atalhos-teclado-minimizado');
    if (estadoSalvo) {
      setMinimizado(JSON.parse(estadoSalvo));
    }
  }, []);

  // Salvar estado no localStorage quando mudar
  const toggleMinimizado = () => {
    const novoEstado = !minimizado;
    setMinimizado(novoEstado);
    localStorage.setItem('atalhos-teclado-minimizado', JSON.stringify(novoEstado));
  };

  // Só mostrar se estiver no modo teclado ou filtro focado
  if (!modoTeclado && !filtroFocado) return null;

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: '20px',
        left: '20px',
        padding: minimizado ? '8px 12px' : '16px',
        backgroundColor: 'black',
        color: 'white',
        zIndex: 1300,
        minWidth: minimizado ? '80px' : '240px',
        maxWidth: minimizado ? '80px' : '280px',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}
    >
      {/* Cabeçalho com botão de minimizar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: minimizado ? '0px' : '12px' }}>
        <Typography variant={minimizado ? "h6" : "h5"} sx={{ fontWeight: 'bold', color: 'white' }}>
          {minimizado ? '⌨️' : '⌨️ Atalhos'}
        </Typography>
        <IconButton 
          size="small" 
          onClick={toggleMinimizado}
          sx={{ 
            color: 'white', 
            padding: '1px',
            minWidth: '20px',
            width: '20px',
            height: '20px'
          }}
        >
          {minimizado ? <ExpandLess sx={{ fontSize: '14px' }} /> : <Close sx={{ fontSize: '14px' }} />}
        </IconButton>
      </Box>

      {!minimizado && (
        <>
          <Box sx={{ marginBottom: '12px' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '6px', fontWeight: 'bold', color: 'white' }}>
              🚀 Início
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <Search sx={{ marginRight: '6px', fontSize: '16px' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Qualquer tecla → Filtro</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <KeyboardArrowDown sx={{ marginRight: '6px', fontSize: '16px' }} />
              <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Setas → Modo teclado</Typography>
            </Box>
          </Box>

          {modoTeclado && (
            <Box sx={{ marginBottom: '12px' }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '6px', fontWeight: 'bold', color: 'white' }}>
                🎮 Navegação
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <KeyboardArrowUp sx={{ marginRight: '6px', fontSize: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>↑ Cima</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <KeyboardArrowDown sx={{ marginRight: '6px', fontSize: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>↓ Baixo</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <KeyboardArrowLeft sx={{ marginRight: '6px', fontSize: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>← Esquerda</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <KeyboardArrowRight sx={{ marginRight: '6px', fontSize: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>→ Direita</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <KeyboardReturn sx={{ marginRight: '6px', fontSize: '16px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Enter - Selecionar</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                <Typography variant="body2" sx={{ marginLeft: '22px', fontSize: '0.875rem' }}>ESC - Sair</Typography>
              </Box>
            </Box>
          )}

          <Box sx={{ marginBottom: '12px' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '6px', fontWeight: 'bold', color: 'white' }}>
              ⚡ Ações
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>F8</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', marginLeft: '8px' }}>→ Concluir pedido</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>F9</Typography>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', marginLeft: '8px' }}>→ Salvar pedido</Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.3)', paddingTop: '8px' }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px', fontWeight: 'bold', color: 'white' }}>
              💡 Dicas
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
              • Produto destacado em azul<br/>
              • Mantém comportamento do clique
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};
